import React, { useEffect } from 'react';
import Button from '../../BaseComponents/Button';
import TextInput from '../../BaseComponents/TextInput';
import TextArea from '../../BaseComponents/TextArea';
import MultiSelect from '../../BaseComponents/MultiSelect';
import { useFormikContext } from 'formik';
import { StepProps } from '../../../pages/complete-profile';
import {
  medicalImplantOptions,
  OnboardingFormValues,
  practicalConsiderationOptions,
  shouldShowFemaleSexHormonesStep
} from '../../../types/membersTypes';
import YesNoDescribeQuestion from './YesNoDescribeQuestion';
import './FormQuestion.scss';

type Option = { key: any; value: any };

type MRISafetyStepProps = {
  setCurrentStep: (step: StepProps) => void;
  saveStepData?: (stepNumber: number) => void;
  populateFormWithSavedData?: () => void;
  membershipType?: string | null;
};

const NONE_KEY = 'None of the above';

const MRISafetyStep: React.FC<MRISafetyStepProps> = ({
  setCurrentStep,
  saveStepData,
  populateFormWithSavedData
}) => {
  const { values, errors, setFieldValue, setFieldError } =
    useFormikContext<OnboardingFormValues>();

  const [hasPopulated, setHasPopulated] = React.useState(false);

  useEffect(() => {
    if (populateFormWithSavedData && !hasPopulated) {
      populateFormWithSavedData();
      setHasPopulated(true);
    }
  }, [populateFormWithSavedData, hasPopulated]);

  const handleExclusiveMultiSelect = (
    option: Option,
    field: 'medical_implants' | 'practical_considerations',
    detailsField: 'medical_implants_details' | 'practical_considerations_details'
  ) => {
    const current = values[field] || [];
    const isSelected = current.some((item) => item.key === option.key);
    let updated: Option[];

    if (option.key === NONE_KEY) {
      updated = isSelected ? [] : [option];
    } else {
      const withoutNone = current.filter((item) => item.key !== NONE_KEY);
      updated = isSelected
        ? withoutNone.filter((item) => item.key !== option.key)
        : [...withoutNone, option];
    }

    setFieldValue(field, updated);
    setFieldError(field, undefined);

    if (!updated.length || updated.every((item) => item.key === NONE_KEY)) {
      setFieldValue(detailsField, '');
      setFieldError(detailsField, undefined);
    }
  };

  const hasImplantSelectionRequiringDetails = (values.medical_implants || []).some(
    (item) => item.key !== NONE_KEY
  );
  const hasPracticalSelectionRequiringDetails = (values.practical_considerations || []).some(
    (item) => item.key !== NONE_KEY
  );

  const validateStepFields = () => {
    const validationErrors: Record<string, string> = {};

    const requireText = (field: keyof OnboardingFormValues, message: string) => {
      if (!String(values[field] ?? '').trim()) {
        validationErrors[field as string] = message;
        setFieldError(field as string, message);
      }
    };

    requireText('reason_for_scan', 'This field is required');
    requireText('current_symptoms', 'This field is required');

    if (values.has_cancer_history !== 'yes' && values.has_cancer_history !== 'no') {
      validationErrors.has_cancer_history = 'Please select yes or no';
      setFieldError('has_cancer_history', 'Please select yes or no');
    } else if (values.has_cancer_history === 'yes' && !values.cancer_history_details?.trim()) {
      validationErrors.cancer_history_details = 'Please provide more details';
      setFieldError('cancer_history_details', 'Please provide more details');
    }

    if (!values.medical_implants?.length) {
      validationErrors.medical_implants = 'Please select at least one option';
      setFieldError('medical_implants', 'Please select at least one option');
    } else if (hasImplantSelectionRequiringDetails && !values.medical_implants_details?.trim()) {
      validationErrors.medical_implants_details = 'Please provide more details';
      setFieldError('medical_implants_details', 'Please provide more details');
    }

    if (!values.practical_considerations?.length) {
      validationErrors.practical_considerations = 'Please select at least one option';
      setFieldError('practical_considerations', 'Please select at least one option');
    } else if (
      hasPracticalSelectionRequiringDetails &&
      !values.practical_considerations_details?.trim()
    ) {
      validationErrors.practical_considerations_details = 'Please provide more details';
      setFieldError('practical_considerations_details', 'Please provide more details');
    }

    return validationErrors;
  };

  const goToTheNextStep = async () => {
    const validationErrors = validateStepFields();
    if (Object.keys(validationErrors).length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const isFemale = values.gender?.key === 'female';
    const hasHormonesStep = shouldShowFemaleSexHormonesStep(isFemale);

    try {
      if (saveStepData) {
        await saveStepData(hasHormonesStep ? 5 : 4);
      }
    } catch {
      return;
    }

    setCurrentStep({
      number: hasHormonesStep ? 6 : 5,
      name: 'Goals'
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className='step-4'>
      <TextArea
        title='What is the primary reason for booking a full body MRI scan with us? *'
        name='reason_for_scan'
        placeholder='Enter your reason for booking'
        rows={4}
        isError={!!errors.reason_for_scan}
        onChange={(e) => {
          setFieldValue('reason_for_scan', e.target.value);
          setFieldError('reason_for_scan', undefined);
        }}
        value={values.reason_for_scan}
      />
      {errors.reason_for_scan && (
        <div className='error-message'>{errors.reason_for_scan as string}</div>
      )}

      <TextArea
        title='Are you currently experiencing any symptoms such as pain, fatigue, or unexplained changes? *'
        name='current_symptoms'
        placeholder='Describe any current symptoms'
        rows={4}
        isError={!!errors.current_symptoms}
        onChange={(e) => {
          setFieldValue('current_symptoms', e.target.value);
          setFieldError('current_symptoms', undefined);
        }}
        value={values.current_symptoms}
      />
      {errors.current_symptoms && (
        <div className='error-message'>{errors.current_symptoms as string}</div>
      )}

      <YesNoDescribeQuestion
        title='Have you ever been diagnosed with cancer?'
        answerField='has_cancer_history'
        detailField='cancer_history_details'
        detailPlaceholder='Include the type, date, and any treatment received'
      />

      <TextArea
        title='Have you had previous medical imaging such as an MRI, CT, or X-ray? If yes, please describe where and approximately when.'
        name='previous_imaging_details'
        placeholder='Describe previous imaging (optional)'
        rows={3}
        onChange={(e) => setFieldValue('previous_imaging_details', e.target.value)}
        value={values.previous_imaging_details}
      />

      <TextArea
        title='Is there a family history of cancer, heart disease, or other serious conditions relevant to your reason for booking? If yes, please describe.'
        name='scan_family_history_details'
        placeholder='Describe relevant family history (optional)'
        rows={3}
        onChange={(e) => setFieldValue('scan_family_history_details', e.target.value)}
        value={values.scan_family_history_details}
      />

      <div className='input-wrapper'>
        <MultiSelect
          title='Medical Implants and Metallic Objects - please select all that apply: *'
          name='medical_implants'
          placeholder='Select all that apply'
          options={medicalImplantOptions}
          selectedOptions={values.medical_implants}
          onChange={(option) =>
            handleExclusiveMultiSelect(option, 'medical_implants', 'medical_implants_details')
          }
          isError={!!errors.medical_implants}
        />
        {errors.medical_implants && (
          <div className='error-message'>{errors.medical_implants as unknown as string}</div>
        )}
      </div>
      {hasImplantSelectionRequiringDetails && (
        <TextInput
          title='If you selected any of the above, please give details — type, location in your body, and when it was inserted.*'
          name='medical_implants_details'
          placeholder='Enter implant details'
          type='text'
          value={values.medical_implants_details}
          isError={!!errors.medical_implants_details}
          errorMessage={errors.medical_implants_details as string}
          onChange={(e) => {
            setFieldValue('medical_implants_details', e.target.value);
            setFieldError('medical_implants_details', undefined);
          }}
        />
      )}

      <div className='input-wrapper'>
        <MultiSelect
          title='Practical and Clinical Considerations - please select all that apply: *'
          name='practical_considerations'
          placeholder='Select all that apply'
          options={practicalConsiderationOptions}
          selectedOptions={values.practical_considerations}
          onChange={(option) =>
            handleExclusiveMultiSelect(
              option,
              'practical_considerations',
              'practical_considerations_details'
            )
          }
          isError={!!errors.practical_considerations}
        />
        {errors.practical_considerations && (
          <div className='error-message'>
            {errors.practical_considerations as unknown as string}
          </div>
        )}
      </div>
      {hasPracticalSelectionRequiringDetails && (
        <TextInput
          title='If you selected any of the above, please give details: *'
          name='practical_considerations_details'
          placeholder='Enter details'
          type='text'
          value={values.practical_considerations_details}
          isError={!!errors.practical_considerations_details}
          errorMessage={errors.practical_considerations_details as string}
          onChange={(e) => {
            setFieldValue('practical_considerations_details', e.target.value);
            setFieldError('practical_considerations_details', undefined);
          }}
        />
      )}

      <div className='submit-btn-wrapper'>
        <Button
          text='Back'
          onClick={() => {
            const isFemale = values.gender?.key === 'female';
            const hasHormonesStep = shouldShowFemaleSexHormonesStep(isFemale);
            setCurrentStep({
              number: hasHormonesStep ? 4 : 3,
              name: 'Medical History'
            });
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          backgroundColor={'#ffffff'}
          color={'#2D334E'}
        />
        <Button text='Next' onClick={goToTheNextStep} />
      </div>
    </div>
  );
};

export default MRISafetyStep;
